/**
 * @techstack/ui - Design Tokens
 *
 * SINGLE SOURCE OF TRUTH for structural design tokens shared between apps.
 * Derived from TekStak's src/styles/variables.css.
 *
 * NOTE: Brand accent colors are NOT included here - each app defines its own:
 *   - TekStak: --accent: #3b82f6 (blue)
 *   - GAS:     --accent: #FA582D (orange)
 *
 * These tokens are structural only (spacing, typography, neutrals, semantics).
 */
// =============================================================================
// GRAY SCALE (neutral palette)
// =============================================================================
export const gray = {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
};
// =============================================================================
// SEMANTIC COLORS (status indicators)
// =============================================================================
export const semantic = {
    success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
    },
    warning: {
        main: '#f59e0b',
        light: '#fcd34d',
        dark: '#d97706',
    },
    danger: {
        main: '#ef4444',
        light: '#fca5a5',
        dark: '#b91c1c',
    },
    info: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#1d4ed8',
    },
};
// =============================================================================
// TYPOGRAPHY SCALE
// =============================================================================
export const typography = {
    fontFamily: {
        sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        mono: "'JetBrains Mono', 'SF Mono', Monaco, monospace",
    },
    fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem',
    },
    fontWeight: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
};
// =============================================================================
// SPACING SCALE
// =============================================================================
export const spacing = {
    0: '0',
    px: '1px',
    '0.5': '0.125rem',
    1: '0.25rem',
    '1.5': '0.375rem',
    2: '0.5rem',
    '2.5': '0.625rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
};
// =============================================================================
// BORDER RADIUS
// =============================================================================
export const radius = {
    none: '0',
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
};
// =============================================================================
// SHADOWS
// =============================================================================
export const shadow = {
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
};
// =============================================================================
// ANIMATION TIMING
// =============================================================================
export const duration = {
    instant: '0ms',
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
};
export const easing = {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
};
// =============================================================================
// CHART COLORS (for shared recharts components)
// =============================================================================
/**
 * Chart palette — the ONE categorical ramp. Series n takes slot n.
 *
 * THIS ORDER IS THE CONTRACT, and it is load-bearing in a way a palette
 * usually is not: the slot a partner, vendor or agency lands in IS its
 * identity. NWN Solutions is indigo on the Dash, indigo on the Partners tab
 * and indigo in the GAS dashboard because all three count from the same
 * array. Reordering these entries silently recolours every one of those.
 *
 * It is HEX, deliberately, where the rest of this file is semantic tokens.
 * A series colour encodes identity, not theme — a reseller must not change
 * colour when the viewer flips to light mode, and must match across two apps
 * that do not share a stylesheet. Themeable surfaces use `gray`/`semantic`
 * above; this ramp is the exception and the only one.
 *
 * It was five copies until 2026-09-18 — three in apps/frontend, two more in
 * this package's own charts — which is how `chartPalette` came to be a
 * DIFFERENT ordering of nearly the same colours, used by almost nothing while
 * the five copies did the real work. The copies' order won because it was the
 * one actually on screen; this constant adopted it rather than renumbering
 * every chart in both apps.
 *
 * Known sharp edge, not yet resolved: slots 3 and 5 are the same green and red
 * the `semantic` tokens use for success and error, so a three-series chart
 * reads "green = good" when green only ever meant "the third one".
 */
export const chartPalette = [
    '#4f46e5', // indigo
    '#0ea5e9', // sky
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#f97316', // orange
    '#ec4899', // pink
    '#14b8a6', // teal
    '#a855f7', // purple
];
/**
 * The same ramp one shade darker, appended for charts that genuinely run past
 * twelve series (the vendor breakdown does). Kept as a continuation rather
 * than a second palette so slot 13 is still deterministic.
 */
export const chartPaletteExtended = [
    ...chartPalette,
    '#6366f1', // indigo-500
    '#0284c7', // sky-600
    '#059669', // emerald-600
    '#d97706', // amber-600
    '#dc2626', // red-600
    '#7c3aed', // violet-600
    '#0891b2', // cyan-600
    '#65a30d', // lime-600
    '#ea580c', // orange-600
    '#db2777', // pink-600
    '#0d9488', // teal-600
    '#9333ea', // purple-600
];
/**
 * Not a series. "Other", unattributed and past-the-end spend are a RESIDUAL,
 * and giving a residual a categorical hue makes it look like one more partner.
 */
export const chartOtherColor = '#6b7280'; // gray-500
// =============================================================================
// TOKEN BUNDLE (for programmatic access)
// =============================================================================
export const tokens = {
    gray,
    semantic,
    typography,
    spacing,
    radius,
    shadow,
    duration,
    easing,
    chartPalette,
};
export default tokens;
